import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/toast.css';
import CssBaseline from '@mui/material/CssBaseline';
import { RtlCacheProvider } from './theme/theme';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { SocketProvider } from './context/SocketContext';
import { NotificationProvider } from './context/NotificationContext';
import { MainLayout } from './layouts/MainLayout';
import Login from './pages/auth/Login';
import Dashboard from './pages/dashboard/Dashboard';
import Appointments from './pages/appointments/Appointments';
import Doctors from './pages/doctors/Doctors';
import Patients from './pages/patients/Patients';
import Scans from './pages/scans/Scans';
import ScanDetails from './pages/scans/ScanDetails';
import ScanCategories from './pages/scanCategories/ScanCategories';
import Stock from './pages/stock/Stock';
import CentralStock from './pages/centralStock/centralStock';
import Profile from './pages/profile/Profile';
import Settings from './pages/settings/Settings';
import PatientDetails from './pages/patients/PatientDetails';
import Radiologists from './pages/radiologists/Radiologists';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import PrivilegeManager from './pages/admin/privileges/PrivilegeManager';
import Register from './pages/auth/Register';
import './i18n/config';
import { useTranslation } from 'react-i18next';
import BranchManager from './pages/admin/branches/BranchManager';
import AppointmentHistory from './pages/appointments/AppointmentHistory';
import AuditLog from './pages/admin/audit/AuditLog';
import RepresentativeManager from './pages/representative/RepresentativeManager';
import { Box, CircularProgress } from '@mui/material';
import Expenses from './pages/expenses/Expenses';
import "./styles/global.css";
import RadiologyTechnicianManager from './pages/RadiologyTechnician/RadiologyTechnicianManager.jsx';
import { AppointmentsProvider } from './context/AppointmentContext.jsx';
import CentralStockItemHistory from './pages/centralStock/centralStockItemHistory.jsx';
import RadiologyTechnicianStats from './pages/RadiologyTechnician/RadiologyTechnicianStats.jsx';
import RadiologistStats from './pages/radiologists/RadiologistStats';
import DoctorStats from "./pages/doctors/doctorStats.jsx"
import StockCategories from './pages/stockCategories/StockCategories';
import Suppliers from './pages/suppliers/Suppliers';
import OrderList from './pages/orderList/OrderList';

// import AddAppointmentPage from './pages/appointments/AddAppointmentPage';
/**
 * Protected Route component that ensures user is authenticated
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  console.log('ProtectedRoute:', { isAuthenticated, loading, user });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <MainLayout>{children}</MainLayout>;
};

const AppContent = () => {
  const { isRtl } = useLanguage();
  const { mode } = useTheme();
  const { i18n } = useTranslation();

  return (
    <RtlCacheProvider isRtl={isRtl}>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AuthProvider>
          <SocketProvider>
            <NotificationProvider>
              <AppointmentsProvider>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  {/* <Route path="/register" element={<Register />} /> */}
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Navigate to="/appointments" replace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/register"
                    element={
                      <ProtectedRoute>
                        <Register />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/appointments"
                    element={
                      <ProtectedRoute>
                        <Appointments daily={true} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/appointments/:id/history"
                    element={
                      <ProtectedRoute>
                        <AppointmentHistory />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/doctors"
                    element={
                      <ProtectedRoute>
                        <Doctors />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="doctors/:id/stats"
                    element={
                      <ProtectedRoute>
                        <DoctorStats />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/patients"
                    element={
                      <ProtectedRoute>
                        <Patients />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/patients/:id"
                    element={
                      <ProtectedRoute>
                        <PatientDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scans"
                    element={
                      <ProtectedRoute>
                        <Scans />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scans/:id"
                    element={
                      <ProtectedRoute>
                        <ScanDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/scan-categories"
                    element={
                      <ProtectedRoute>
                        <ScanCategories />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/stock"
                    element={
                      <ProtectedRoute>
                        <Stock />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/central-stock"
                    element={
                      <ProtectedRoute>
                        <CentralStock />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/central-stock/:itemId/history"
                    element={
                      <ProtectedRoute>
                        <CentralStockItemHistory />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/expenses"
                    element={
                      <ProtectedRoute>
                        <Expenses />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/radiologists"
                    element={
                      <ProtectedRoute>
                        <Radiologists />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/radiologists/:id/statistics"
                    element={
                      <ProtectedRoute>
                        <RadiologistStats />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/radiology-technicians"
                    element={
                      <ProtectedRoute>
                        <RadiologyTechnicianManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/radiology-technicians/:id"
                    element={
                      <ProtectedRoute>
                        <RadiologyTechnicianStats />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/privileges"
                    element={
                      <ProtectedRoute>
                        <PrivilegeManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/branches"
                    element={
                      <ProtectedRoute>
                        <BranchManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/audit"
                    element={
                      <ProtectedRoute>
                        <AuditLog />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/representatives"
                    element={
                      <ProtectedRoute>
                        <RepresentativeManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/stock-categories"
                    element={
                      <ProtectedRoute>
                        <StockCategories />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/suppliers"
                    element={
                      <ProtectedRoute>
                        <Suppliers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/order-list"
                    element={
                      <ProtectedRoute>
                        <OrderList />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </AppointmentsProvider>
            </NotificationProvider>
          </SocketProvider>
        </AuthProvider>
        <ToastContainer
          position={isRtl ? 'top-left' : 'top-right'}
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={isRtl}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={mode}
          toastClassName={isRtl ? 'toast-rtl' : ''}
          bodyClassName={isRtl ? 'toast-body-rtl' : ''}
          enableMultiContainer={false}
          limit={3}
        />
      </Router>
    </RtlCacheProvider>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <CssBaseline />
          <AppContent />
        </LocalizationProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
};

export default App; 